import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-hot-toast";

import {
  createNewJob,
  getCustomerCompany,
} from "../../../../../services/staffingService";


import { useParams, useHistory } from "react-router-dom";
import { ArrowLeft, Briefcase } from "lucide-react";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import DashboardSidebar from "../../agencyDashboardSidebar";
import NewCustomer from "../customerform";

// Import step components
import ProgressSteps from "./ProgressSteps";
import Step1ServiceDetails from "./Step1ServiceDetails";
import Step2LocationMap from "./Step2LocationMap";
import Step3ReviewSubmit from "./Step3ReviewSubmit";

// Validation schema - preserve exact validation from original
const schema = yup
  .object({
    serviceType: yup.string().required("Service Type is required."),
    hourlyPayRate: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .required("Hourly offer price is required.")
      .positive("Hourly offer price must be positive.")
      .min(7, "Hourly offer price must be at least $7")
      .max(250, "Hourly offer price cannot exceed $250"),
    location: yup.string().required("Job location is required."),
    shifts: yup
      .array()
      .of(
        yup.object().shape({
          date: yup.date().required("Date is required"),
          startTime: yup
            .date()
            .required("Start time is required")
            .test(
              "is-future",
              "Shift start time cannot be in the past",
              function (value) {
                const { date } = this.parent;
                const startTime = moment(value).set({
                  year: moment(date).year(),
                  month: moment(date).month(),
                  date: moment(date).date(),
                });
                return startTime.isSameOrAfter(moment(), "minute");
              }
            ),
          endTime: yup
            .date()
            .required("End time is required")
            .test(
              "is-future",
              "Shift end time cannot be in the past",
              function (value) {
                const { date } = this.parent;
                const endTime = moment(value).set({
                  year: moment(date).year(),
                  month: moment(date).month(),
                  date: moment(date).date(),
                });
                return endTime.isSameOrAfter(moment(), "minute");
              }
            )
            .test(
              "is-after-start",
              "Shift end time must be after shift start time",
              function (value) {
                const { startTime } = this.parent;
                return moment(value).isAfter(moment(startTime), "minute");
              }
            ),
          staffsRequired: yup
            .number()
            .required("Staffs required is required")
            .min(1, "At least 1 staff is required"),
        })
      )
      .min(1, "At least one shift is required"),
    memo: yup
      .string()
      .max(255, "Memo cannot exceed 255 characters")
      .matches(/^[a-zA-Z0-9 .,!?]*$/, "Memo contains invalid characters")
      .nullable(),
  })
  .required();

const CreateJobForm = () => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
  });

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [customerCompanyLocal, setCustomerCompanyLocal] = useState({});
  const [showSideDrawer, setShowSideDrawer] = useState(false);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [errType, setErrType] = useState("");

  const history = useHistory();
  const { providerCompanyId, customerCompanyLocalId } = useParams();
  const mapRef = useRef(null);
  const postsMarkersRef = useRef([]);
  const selectedMarkerRef = useRef(null);

  const totalSteps = 3;
  const stepTitles = [
    "Service Details",
    "Location & Posts",
    "Review & Submit"
  ];

  useEffect(() => {
    getCustomerCompanyById();
  }, []);

  const getCustomerCompanyById = async () => {
    const resp = await getCustomerCompany(providerCompanyId, customerCompanyLocalId);
    setCustomerCompanyLocal(resp.data.customerCompany);
  };

  // Service type options
  const serviceList = [
    "standing_post",
    "mobile_patrol",
    "workplace_security",
    "event_security",
    "executive_protection",
    "emergency",
  ];

  const getValueAtPosition = (position) => {
    const index = parseInt(position, 10) - 1;
    return index >= 0 && index < serviceList.length
      ? serviceList[index]
      : "Invalid position";
  };

  // Location handling functions - preserve original logic
  const onPlaceSelected = (place) => {
    const addressComponents = place.address_components;
    const locationData = {
      formatted_address: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      state: "",
      city: "",
      zipcode: "",
    };

    addressComponents.forEach((component) => {
      const types = component.types;
      if (types.includes("administrative_area_level_1")) {
        locationData.state = component.long_name;
      }
      if (types.includes("locality")) {
        locationData.city = component.long_name;
      }
      if (types.includes("postal_code")) {
        locationData.zipcode = component.long_name;
      }
    });

    setSelectedLocation(locationData);
    setValue("location", locationData.formatted_address);
  };

  // Shift management functions - preserve original logic
  const addShift = () => {
    const shifts = getValues("shifts") || [];
    setValue("shifts", [
      ...shifts,
      { date: null, startTime: null, endTime: null, staffsRequired: 1 },
    ]);
  };

  const removeShift = (index) => {
    const shifts = getValues("shifts") || [];
    shifts.splice(index, 1);
    setValue(
      "shifts",
      shifts.length > 0
        ? shifts
        : [{ date: null, startTime: null, endTime: null, staffsRequired: 1 }]
    );
  };

  const handleShiftChange = (index, updatedShift) => {
    const shifts = getValues("shifts") || [];
    shifts[index] = updatedShift;
    setValue("shifts", shifts);
  };

  // Navigation functions
  const goToNextStep = async () => {
    let isValid = false;
    
    // Validate current step
    if (currentStep === 1) {
      isValid = await trigger(["serviceType", "hourlyPayRate", "shifts", "memo"]);
    } else if (currentStep === 2) {
      isValid = await trigger(["location"]);
    }

    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Clear location and posts
  const handleCancel = () => {
    setSelectedLocation(null);
    setValue("location", "");
    setPosts([]);
    postsMarkersRef.current.forEach((marker) => marker.setMap(null));
    postsMarkersRef.current = [];
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.setMap(null);
      selectedMarkerRef.current = null;
    }
  };

  // Prevent form submission on Enter key press unless on final step
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && currentStep !== 3) {
      e.preventDefault();
      // If on step 1 or 2, advance to next step instead
      if (currentStep < 3) {
        goToNextStep();
      }
    }
  };

  // Submit functions - preserve original logic
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const newWorkOrder = {
        ...data,
        customerCompanyGlobalId: customerCompanyLocal.customer_company_id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        status: "published",
        ...selectedLocation,
        service: getValueAtPosition(data.serviceType),
        posts,
      };
      
      console.log("Submitting new work order:", newWorkOrder);

      
      let resp = await createNewJob(
        newWorkOrder,
        providerCompanyId,
        customerCompanyLocalId
      );
      
      history.push(`/provider-company/${resp.data.providerCompanyId}/jobs`);
      

      setLoading(false);
    } catch (ex) {
      setLoading(false);
      if (ex.response && ex.response.status === 400) {
        console.log(ex.response.data);
        toast.error(ex.response.data.error);
        setErr(ex.response.data.error);
        setErrType("advance");
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Default shifts
  const defaultShifts = [
    {
      date: null,
      startTime: null,
      endTime: null,
      staffsRequired: 1,
    },
  ];

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1ServiceDetails
            register={register}
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            addShift={addShift}
            removeShift={removeShift}
            handleShiftChange={handleShiftChange}
            defaultShifts={defaultShifts}
          />
        );
      case 2:
        return (
          <Step2LocationMap
            register={register}
            control={control}
            errors={errors}
            setValue={setValue}
            selectedLocation={selectedLocation}
            onPlaceSelected={onPlaceSelected}
            handleCancel={handleCancel}
            posts={posts}
            setPosts={setPosts}
            mapRef={mapRef}
            postsMarkersRef={postsMarkersRef}
            selectedMarkerRef={selectedMarkerRef}
          />
        );
      case 3:
        return (
          <Step3ReviewSubmit
            watch={watch}
            selectedLocation={selectedLocation}
            posts={posts}
            customerCompanyLocal={customerCompanyLocal}
            serviceList={serviceList}
            errors={errors}
            err={err}
            errType={errType}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-inter">
      <DashboardSidebar isOpen={showSideDrawer} setIsOpen={setShowSideDrawer} />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">

          {/* Back button */}
          <button
            onClick={() => history.push(`/provider-company/${providerCompanyId}/crm`)}
            className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Customers
          </button>

          {/* Page header */}
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Briefcase size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">New Work Order</h1>
              <p className="text-sm text-gray-500">
                {customerCompanyLocal.companyFriendlyName
                  ? `Creating for ${customerCompanyLocal.companyFriendlyName}`
                  : "Create a new work order"}
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Progress Steps */}
              <div className="p-6 pb-0">
                <ProgressSteps
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  stepTitles={stepTitles}
                />
              </div>

              <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleFormKeyDown}>
                {/* Step Content */}
                <div className="p-6">
                  {renderCurrentStep()}
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center px-6 pb-6 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    disabled={currentStep === 1}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      currentStep === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Previous
                  </button>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => history.push(`/provider-company/${providerCompanyId}/crm`)}
                      className="px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>

                    {currentStep < totalSteps ? (
                      <button
                        type="button"
                        onClick={goToNextStep}
                        className="px-5 py-2 text-white rounded-md text-sm font-medium transition-colors"
                        style={{ backgroundColor: "var(--primary)" }}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={loading}
                        className="px-5 py-2 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        style={{ backgroundColor: "var(--primary)" }}
                      >
                        {loading ? "Submitting..." : "Post Job"}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="w-12 h-12 border-4 border-t-primary rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default CreateJobForm;